/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useApiKeySubmit.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for handling form submission, validation, and API calls.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../store/auth';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { createApiKey, updateApiKey, deleteApiKey } from '../api';
import { ApiKeyFormState } from './useApiKeyForm';
import { ApiKey } from '../types';

export function useApiKeySubmit(
  mode: 'create' | 'edit',
  id: string | undefined,
  formData: ApiKeyFormState,
  originalData: ApiKeyFormState,
  hasChanges: boolean,
  setOriginalData: (data: ApiKeyFormState) => void,
  setGeneratedApiKey: (key: string | null) => void,
) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { showSuccess, showError, showInfo } = useNotifications();
  const { t } = useI18n();
  const tenantSlug = user?.tenant?.slug ?? t('apiKeys.defaultTenant');
  const userId = user?.id;

  const [saving, setSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};

    if (!formData.name || !formData.name.trim()) {
      newErrors.name = t('apiKeys.nameRequired');
    }

    if (formData.expiresAt && new Date(formData.expiresAt) <= new Date()) {
      newErrors.expiresAt = t('apiKeys.expirationDateFuture');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, t]);

  const normalizeTenantId = useCallback((tenantId: string | number | null): number | null => {
    if (tenantId === null || tenantId === undefined || tenantId === '') {
      return null;
    }
    if (typeof tenantId === 'string') {
      const trimmed = tenantId.trim();
      if (trimmed === '' || trimmed === 'null' || trimmed === 'undefined') {
        return null;
      }
      const numericValue = Number.parseInt(trimmed, 10);
      if (Number.isFinite(numericValue) && numericValue > 0) {
        return numericValue;
      }
      return null;
    }
    if (typeof tenantId === 'number') {
      if (Number.isFinite(tenantId) && tenantId > 0) {
        return tenantId;
      }
      return null;
    }
    return null;
  }, []);

  const handleSubmit = useCallback(
    async (event?: FormEvent<HTMLFormElement>): Promise<ApiKey | undefined> => {
      if (event) {
        event.preventDefault();
      }

      // hasChanges validation removed

      if (!validateForm()) {
        showError(t('apiKeys.errorFormValidation'));
        return undefined;
      }

      try {
        setSaving(true);

        const submitData = {
          name: formData.name.trim(),
          description: formData.description?.trim() || '',
          tenantId: normalizeTenantId(formData.tenantId),
          permissions: formData.permissions || {},
          isActive: formData.isActive,
          expiresAt: formData.expiresAt && formData.expiresAt.trim() ? formData.expiresAt : null,
        };

        const savedApiKey =
          mode === 'create'
            ? await createApiKey(submitData, tenantSlug, userId)
            : await updateApiKey(Number(id), submitData, tenantSlug, userId);

        showSuccess(mode === 'create' ? t('apiKeys.apiKeyCreated') : t('apiKeys.apiKeyUpdated'));

        setOriginalData({ ...formData, permissions: { ...formData.permissions } });

        if (mode === 'create' && savedApiKey?.key) {
          setGeneratedApiKey(savedApiKey.key);
        } else if (mode === 'edit' && savedApiKey?.key) {
          setGeneratedApiKey(savedApiKey.key);
        }

        return savedApiKey;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : t('apiKeys.unknownError');
        showError(`${t('apiKeys.errorSaving')}: ${errorMessage}`);
        return undefined;
      } finally {
        setSaving(false);
      }
    },
    [
      mode,
      id,
      formData,
      hasChanges,
      validateForm,
      normalizeTenantId,
      tenantSlug,
      userId,
      setOriginalData,
      setGeneratedApiKey,
      showSuccess,
      showError,
      showInfo,
      t,
    ],
  );

  const handleCancel = useCallback(() => {
    setErrors({});
  }, []);

  const handleDeleteClick = useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!id || id === 'new') return;

    setIsDeleting(true);
    try {
      await deleteApiKey(Number(id), tenantSlug, userId);
      showSuccess(t('apiKeys.apiKeyDeleted') || t('apiKeys.deleteSuccess'));
      setShowDeleteModal(false);
      navigate('/admin/admin/app-keys');
    } catch (error) {
      showError(error.message || t('apiKeys.deleteError') || 'Failed to delete API key');
    } finally {
      setIsDeleting(false);
    }
  }, [id, tenantSlug, userId, navigate, showSuccess, showError, t]);

  return {
    saving,
    isDeleting,
    showDeleteModal,
    errors,
    setErrors,
    handleSubmit,
    handleCancel,
    handleDeleteClick,
    handleDeleteCancel,
    handleDelete,
  };
}

